import React, { Dispatch, FC, ReactNode, createContext, useContext, useEffect } from 'react'
import { merge } from 'lodash'
import { useImmerReducer } from 'use-immer'
import Cookies from 'js-cookie'

import { IOrder } from 'checkout/types/IOrder'
import { useApplePay, useOrder, usePayments } from 'checkout/hooks'
import usePromotion, { IActivatePromoProps } from 'checkout/hooks/usePromotion'
import { IBillingAddress } from 'checkout/components/PaymentMethodPanel/NewCardNewAddressForm'
import useShippingAddresses, {
  IGuestShippingAddress,
  IShippingAddress,
} from 'checkout/hooks/useShippingAddresses'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import {
  ICheckoutState,
  IErrorAction,
  INITIAL_CHECKOUT_STATE,
  IOrderSuccessAction,
  IUserTypeAction,
  IPanelEditAction,
  IPanelState,
  IPanelUpdateAction,
  PanelNames,
  checkoutReducer,
} from 'checkout/containers/CheckoutStateReducer'
import { useBraintree } from 'checkout/hooks/useBraintree'
import { useRouter } from 'next/router'
import { useUser } from 'shared/hooks/useUser'
import { STORAGE } from 'shared/enums/SitePreferences'
import { useOrderRTK } from 'checkout/hooks/useOrderRTK'
import { useShippingAddressRTK } from 'checkout/hooks/useShippingAddressRTK'
import { Order } from 'shared/types/Order'
import { usePaymentsRTK } from 'checkout/hooks/usePaymentsRTK'
import { Address } from 'shared/types/Address'
import { ActivatePromotion, usePromotionRTK } from 'checkout/hooks/usePromotionRTK'
import { Promotion } from 'shared/types/Promotion'
import { useExpressPayRTK } from 'checkout/hooks/useExpressPayRTK'
import { useCheckoutWithRTK } from 'checkout/hooks/useCheckoutWithRTK'

interface ICheckoutContext extends ICheckoutState {
  ApplePayButton?: FC
  ExpressPayButton?: FC
  addNewCreditCard(address: IBillingAddress): void
  addNewCreditCardRTK(address: Address): void
  activatePromotion(props: IActivatePromoProps): void
  activatePromotionRTK(props: ActivatePromotion): void
  buyOrderRTK(): void
  changeOrderOptionRTK(props): void
  changeShippingServiceLevel(shippingServiceLevel: string): void
  changeShippingServiceLevelRTK(shippingServiceLevel: string): void
  connectAddressToOrder(addressId: number): void
  connectAddressToOrderRTK(addressId: number): void
  continueAsGuest(): void
  createGuestOrderWithShippingAddress(address: IGuestShippingAddress): void
  createGuestOrderWithShippingAddressRTK(address: IGuestShippingAddress): void
  createOrder(): void
  createOrderRTK(): void
  createShippingAddress(address: IShippingAddress): void
  createShippingAddressForOrderRTK(address: IShippingAddress): void
  displayCheckoutRTK: boolean
  editPanel(panelName: PanelNames): void
  getCreditCards(): void
  getPriceBreakdownRTK(): void
  initBraintree: () => Promise<void>
  purchaseOrder(): void
  selectAffirm(): void
  selectAffirmRTK(processorName: string, shippingAddress: Address): void
  selectAfterPay(): void
  selectAfterPayRTK(): void
  selectKlarna(): void
  selectKlarnaRTK(): void
  selectCreditCard(creditCardId: number): void
  selectCreditCardRTK(creditCardId: number): void
  selectPayPal(): void
  selectPayPalv2(): void
  selectPayPalRTK(): void
  removePromotion(orderNumber?: number): void
  removePromotionRTK(promotion: Promotion): void
  updateOrderCurrencyRTK(): void
  updatePanel(panelName: PanelNames, panelState: Partial<IPanelState>): void
  verifyCreditCard(): void
  verifyCreditCardRTK(): void
}
export const CheckoutContext = createContext<ICheckoutContext>({
  ...INITIAL_CHECKOUT_STATE,
  addNewCreditCard: () => {},
  addNewCreditCardRTK: () => {},
  activatePromotion: () => {},
  activatePromotionRTK: () => {},
  buyOrderRTK: () => {},
  changeOrderOptionRTK: () => {},
  changeShippingServiceLevel: () => {},
  changeShippingServiceLevelRTK: () => {},
  connectAddressToOrder: () => {},
  connectAddressToOrderRTK: () => {},
  continueAsGuest: () => {},
  createGuestOrderWithShippingAddress: () => {},
  createGuestOrderWithShippingAddressRTK: () => {},
  createOrder: () => {},
  createOrderRTK: () => {},
  createShippingAddress: () => {},
  createShippingAddressForOrderRTK: () => {},
  displayCheckoutRTK: false,
  editPanel: () => {},
  getCreditCards: () => {},
  getPriceBreakdownRTK: () => {},
  initBraintree: () => Promise.resolve(),
  purchaseOrder: () => {},
  selectAffirm: () => {},
  selectAffirmRTK: () => {},
  selectAfterPay: () => {},
  selectAfterPayRTK: () => {},
  selectKlarna: () => {},
  selectKlarnaRTK: () => {},
  selectCreditCard: () => {},
  selectCreditCardRTK: () => {},
  selectPayPal: () => {},
  selectPayPalv2: () => {},
  selectPayPalRTK: () => {},
  removePromotion: () => {},
  removePromotionRTK: () => {},
  updateOrderCurrencyRTK: () => {},
  updatePanel: () => {},
  verifyCreditCard: () => {},
  verifyCreditCardRTK: () => {},
})
export const useCheckoutContext = () => useContext(CheckoutContext)

interface ICheckoutContextProviderProps {
  children: ReactNode
  errorFromServer?: string
  gqlOrderFromServer?: IOrder
  restOrderFromServer?: Order
}

const CheckoutContextProvider = ({
  children,
  errorFromServer,
  gqlOrderFromServer,
  restOrderFromServer,
}: ICheckoutContextProviderProps) => {
  const isGuestCheckout = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const isCreditCardVerificationEnabled = useFeatureFlag(FeatureFlag.CREDIT_CARD_VERIFICATION)
  const { displayCheckoutRTK } = useCheckoutWithRTK()
  const { currentUserType } = useUser()

  const [state, dispatch] = useImmerReducer(
    checkoutReducer,
    merge(INITIAL_CHECKOUT_STATE, {
      creditCardVerificationEnabled: isCreditCardVerificationEnabled,
      isGuestCheckout,
      currentUserType,
    }),
  )
  const { activatePromotion, removePromotion } = usePromotion({
    dispatch,
    promotion: state.order?.promotion,
  })
  const { activatePromotionRTK, removePromotionRTK } = usePromotionRTK({
    dispatch,
    order: state.order,
  })
  const { createOrder, purchaseOrder } = useOrder({
    dispatch,
    displayCheckoutRTK,
    order: state.order,
  })
  const { buyOrderRTK, changeOrderOptionRTK, createOrderRTK, getPriceBreakdownRTK } = useOrderRTK({
    dispatch,
    order: state.order,
  })
  const {
    addNewCreditCard,
    getCreditCards,
    selectAffirm,
    selectAfterPay,
    selectKlarna,
    selectPayPalv2,
    selectCreditCard,
    verifyCreditCard,
  } = usePayments({
    dispatch,
    order: state.order,
  })
  const {
    addNewCreditCardRTK,
    selectAffirmRTK,
    selectAfterPayRTK,
    selectCreditCardRTK,
    selectKlarnaRTK,
    selectPayPalRTK,
    updateOrderCurrencyRTK,
    verifyCreditCardRTK,
  } = usePaymentsRTK({
    dispatch,
    order: state.order,
  })
  const {
    changeShippingServiceLevel,
    connectAddressToOrder,
    createShippingAddress,
    createGuestOrderWithShippingAddress,
  } = useShippingAddresses({ dispatch, order: state.order })
  const {
    changeShippingServiceLevelRTK,
    connectAddressToOrderRTK,
    createGuestOrderWithShippingAddressRTK,
    createShippingAddressForOrderRTK,
  } = useShippingAddressRTK({
    dispatch,
    order: state.order,
  })
  const { ApplePayButton } = useApplePay({ dispatch, displayCheckoutRTK, order: state.order })
  const { ExpressPayButton } = useExpressPayRTK({
    dispatch,
    displayCheckoutRTK,
    order: state.order,
  })
  const { initBraintree, selectPayPal } = useBraintree({ dispatch, order: state.order })

  const router = useRouter()
  const klarnaReason = router.query.klarna as string

  // Init items passed from Next getServerSideProps
  useEffect(() => {
    // BUY1-2604
    // When navigating back and forward between checkout and PDP from Guest checkout
    // with a saved shipping address tries to retrieve guest checkout order number
    // that no longer exists because loading the PDP initiates a logout of that
    // guest user.
    if (
      router?.query?.orderNumber &&
      !(gqlOrderFromServer || restOrderFromServer) &&
      !errorFromServer
    ) {
      const cartQueryParams = window.sessionStorage.getItem('recentAdd') || ''
      window.location.href = cartQueryParams
    }
    if (gqlOrderFromServer || restOrderFromServer) {
      const dispatchOrderSuccess = dispatch as Dispatch<IOrderSuccessAction>
      // Adding klarnaReason here so that order autosubmits even if multiple
      // shipping options are available
      dispatchOrderSuccess({
        type: 'orderRequestSuccess',
        payload: {
          ...(displayCheckoutRTK ? restOrderFromServer : gqlOrderFromServer),
          klarnaReason,
        },
      })
    }
    if (errorFromServer) {
      const dispatchOrderFailure = dispatch as Dispatch<IErrorAction>
      dispatchOrderFailure({ type: 'orderRequestFailure', payload: errorFromServer })
    }
  }, [displayCheckoutRTK])

  // pass on userType
  useEffect(() => {
    if (currentUserType !== state.userType) {
      const dispatchUserTypeUpdate = dispatch as Dispatch<IUserTypeAction>
      dispatchUserTypeUpdate({ type: 'UserTypeUpdate', payload: currentUserType })
    }
  }, [currentUserType])

  // If STORAGE.CHECKOUT_ERROR cookie has error message from server, show it.
  useEffect(() => {
    const checkoutError = Cookies.getJSON(STORAGE.CHECKOUT_ERROR)
    if (checkoutError && checkoutError.messages?.length > 0) {
      const dispatchOrderFailure = dispatch as Dispatch<IErrorAction>
      dispatchOrderFailure({ type: 'orderRequestFailure', payload: checkoutError.messages[0] })
    }
    Cookies.remove(STORAGE.CHECKOUT_ERROR, { path: '/' })
  }, [])

  const dispatchPanelUpdate = dispatch as Dispatch<IPanelUpdateAction>
  const updatePanel = (panelName: PanelNames, panelState: Partial<IPanelState>) =>
    dispatchPanelUpdate({
      type: 'panelUpdate',
      payload: { panelName, ...panelState },
    })
  const dispatchPanelEdit = dispatch as Dispatch<IPanelEditAction>
  const editPanel = (panelName: PanelNames) =>
    dispatchPanelEdit({
      type: 'panelEdit',
      payload: panelName,
    })

  // auth panel actions
  const continueAsGuest = () => {
    if (isGuestCheckout) {
      return dispatch({ type: 'continueAsGuest' })
    }
  }

  const checkoutContextValues = {
    ...state,
    ApplePayButton,
    ExpressPayButton,
    activatePromotion,
    activatePromotionRTK,
    addNewCreditCard,
    addNewCreditCardRTK,
    buyOrderRTK,
    changeOrderOptionRTK,
    changeShippingServiceLevel,
    changeShippingServiceLevelRTK,
    connectAddressToOrder,
    connectAddressToOrderRTK,
    continueAsGuest,
    createGuestOrderWithShippingAddress,
    createGuestOrderWithShippingAddressRTK,
    createOrder,
    createOrderRTK,
    createShippingAddress,
    createShippingAddressForOrderRTK,
    displayCheckoutRTK,
    editPanel,
    getCreditCards,
    getPriceBreakdownRTK,
    initBraintree,
    purchaseOrder,
    removePromotion,
    removePromotionRTK,
    selectAffirm,
    selectAffirmRTK,
    selectAfterPay,
    selectAfterPayRTK,
    selectKlarna,
    selectKlarnaRTK,
    selectCreditCard,
    selectCreditCardRTK,
    selectPayPal,
    selectPayPalv2,
    selectPayPalRTK,
    updateOrderCurrencyRTK,
    updatePanel,
    verifyCreditCard,
    verifyCreditCardRTK,
  }
  return (
    <CheckoutContext.Provider value={checkoutContextValues}>{children}</CheckoutContext.Provider>
  )
}

export default CheckoutContextProvider
